import axios from "axios";
import { API_URL } from "./Urls";
import jwt_decode from "jwt-decode";
import { parseCookies, setCookie, destroyCookie } from "nookies";

export const api = axios.create({
  baseURL: API_URL,
  params: {
    t: new Date().getTime(),
  },
});

// Verificação de Token
export const VerifyIfTokenNotExpired = (token) => {
  let tokenDecoded = jwt_decode(token);

  if (!sessionStorage.getItem("plan_id")) {
    sessionStorage.setItem("plan_id", tokenDecoded.plan_id);
  }
  return tokenDecoded.exp > Date.now() / 1000;
};

// Lista de endpoints que não requerem autenticação
// NOTA: "/auth/" foi substituído por endpoints específicos para que
// /auth/me/ (que requer token) não seja tratado como público
const publicEndpoints = [
  "/security/",
  "/auth/login/",
  "/auth/login_verify/",
  "/auth/turnstile/",
  "/auth/complete_registration/",
  "/auth/refresh/",
  "requests/index/login/",
  "api.tomtom.com",
  "celery-progress/",
  "users/validate_cpf",
  "users/create_first_admin",
  "viacep.com.br",
  "hereapi.com",
  "app.wabotty.com.br",
  "api/v1/google-api",
  "/api/v1/account/activate",
  "/api/v1/account/quick-signup",
  "/api/v1/whitelabel-config",
  "/api/wabotty/public/",
  "customer-payments/create_subscription",
];

const isActivationRoute = () => {
  const currentPath = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);

  return currentPath === "/activate" &&
         searchParams.has("token") &&
         searchParams.has("email");
};

const isCheckoutRoute = () => {
  const currentPath = window.location.pathname;
  return currentPath === "/checkout" || currentPath === "/signup";
};

const isPublicPlaygroundRoute = () => {
  const currentPath = window.location.pathname;
  return currentPath.startsWith("/testar");
};

const isWhatsAppConnectRoute = () => {
  const currentPath = window.location.pathname;
  return currentPath.startsWith("/conectar");
};

const isSpecialRoute = () => {
  return isActivationRoute() || isCheckoutRoute() || isPublicPlaygroundRoute() || isWhatsAppConnectRoute();
};

// Interceptor de requisição
api.interceptors.request.use(
  async function (config) {
    try {
      let isPublicEndpoint = publicEndpoints.some((endpoint) =>
        config.url.includes(endpoint)
      );

      if (!isPublicEndpoint) {
        try {
          const { token } = parseCookies();
          if (token && token !== 'undefined') {
            config.headers.Authorization = `Bearer ${token}`;
          } else {
            if (!isSpecialRoute()) {
              localStorage.clear();
              window.location.href = "/login";
              return;
            }
          }
        } catch (error) {
          if (!isSpecialRoute()) {
            localStorage.clear();
            window.location.href = "/login";
            return;
          }
        }
      }

      return config;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  function (error) {
    return Promise.reject(error);
  }
);

// ========================================
// Interceptor de resposta com refresh token
// ========================================

// Flag para evitar loops de refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Helper: identifica se 401 é de token expirado (SimpleJWT) vs erro de negócio
// CRITICO: O backend usa 401 em 30+ locais para erros de negócio (limite de agentes,
// permissão, webhook, etc). Só devemos tentar refresh quando o SimpleJWT retorna
// code: "token_not_valid". Qualquer outro 401 é propagado normalmente.
const isTokenExpiredError = (error) => {
  const data = error.response?.data;
  if (!data) return false;

  if (data.code === "token_not_valid") return true;
  if (data.detail === "Authentication credentials were not provided.") return true;

  return false;
};

const forceLogout = () => {
  localStorage.clear();
  sessionStorage.clear();
  destroyCookie(null, "token", { path: "/" });
  destroyCookie(null, "refresh_token", { path: "/" });
  destroyCookie(null, "MA_AID", { path: "/" });
  window.location.href = "/login";
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Só tentar refresh se for 401 de TOKEN EXPIRADO (SimpleJWT)
    // 401 de negócio (limite de agentes, permissão, etc) é propagado normalmente
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      isTokenExpiredError(error)
    ) {
      // Se já está fazendo refresh, enfileirar a request para retry após refresh
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const { refresh_token } = parseCookies();

      if (!refresh_token) {
        // Sem refresh token, fazer logout (exceto rotas especiais)
        isRefreshing = false;
        processQueue(error, null);
        if (!isSpecialRoute()) {
          forceLogout();
        }
        return Promise.reject(error);
      }

      try {
        // Tentar renovar o token usando axios puro (não o api interceptado)
        const response = await axios.post(`${API_URL}auth/refresh/`, {
          refresh: refresh_token,
        });

        const { access, refresh } = response.data;

        // Salvar novo access token
        setCookie(undefined, "token", access, {
          maxAge: 60 * 60 * 24 * 14,
          path: "/",
          sameSite: "Lax",
        });

        // Salvar novo refresh token (ROTATE_REFRESH_TOKENS=True gera novo a cada refresh)
        if (refresh) {
          setCookie(undefined, "refresh_token", refresh, {
            maxAge: 60 * 60 * 24 * 14,
            path: "/",
            sameSite: "Lax",
          });
        }

        // Retry da request original e de todas enfileiradas
        originalRequest.headers.Authorization = `Bearer ${access}`;
        processQueue(null, access);
        return api(originalRequest);
      } catch (refreshError) {
        // Distinguir entre "refresh token inválido" vs "servidor indisponível"
        const isServerUnavailable =
          !refreshError.response ||                         // Network error (ECONNREFUSED, timeout)
          refreshError.response.status >= 500;              // 502, 503, 500, etc.

        if (isServerUnavailable) {
          // Servidor fora do ar (deploy, restart, etc) — NÃO deslogar
          // Apenas rejeitar os requests, usuário continua logado
          processQueue(refreshError, null);
          return Promise.reject(refreshError);
        }

        // Refresh token realmente inválido/expirado (401 do servidor) — logout
        processQueue(refreshError, null);
        if (!isSpecialRoute()) {
          forceLogout();
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);
